import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { PenLine, CheckCircle2, Eraser } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("mgbApprenantToken")}` });
const frDate = (iso) => new Date(iso).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

const SignaturePad = ({ onCancel, onConfirm }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);

  const pos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - rect.left) * (c.width / rect.width), y: (src.clientY - rect.top) * (c.height / rect.height) };
  };
  const start = (e) => { drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.strokeStyle = "#003087";
    const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setEmpty(false);
  };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setEmpty(true); };

  return (
    <div className="mt-3 p-3 rounded-lg border border-[#F07B1D] bg-[#FFF8F1]" data-testid="signature-pad">
      <p className="text-xs text-gray-600 mb-2">Signez dans le cadre ci-dessous (souris ou doigt). Votre signature, l'heure et votre adresse IP sont enregistrées.</p>
      <canvas
        ref={canvasRef} width={520} height={160}
        className="w-full max-w-[520px] h-[160px] bg-white border border-gray-300 rounded-md touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        data-testid="signature-canvas"
      />
      <div className="flex flex-wrap gap-2 mt-2">
        <Button size="sm" className="bg-[#F07B1D] hover:bg-[#D96A0F] text-white font-bold" disabled={empty} onClick={() => onConfirm(canvasRef.current.toDataURL("image/png"))} data-testid="signature-confirm">
          <CheckCircle2 className="h-4 w-4 mr-1" /> Valider ma signature
        </Button>
        <Button size="sm" variant="outline" onClick={clear} data-testid="signature-clear"><Eraser className="h-4 w-4 mr-1" /> Effacer</Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>Annuler</Button>
      </div>
    </div>
  );
};

export const EmargementApprenant = () => {
  const [items, setItems] = useState([]);
  const [signing, setSigning] = useState(null);
  const today = new Date().toISOString().slice(0, 10);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/apprenant/emargements`, { headers: authHeaders() });
      setItems(res.data);
    } catch { /* silencieux : pas de dossier lié */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sign = async (dossierId, slotKey, signature) => {
    try {
      await axios.post(`${API}/apprenant/emargements/${dossierId}/${slotKey}/sign`, { signature }, { headers: authHeaders() });
      toast.success("Présence signée — merci !");
      setSigning(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signature impossible");
    }
  };

  if (items.length === 0) return null;
  return (
    <Card data-testid="emargement-section">
      <CardHeader>
        <CardTitle className="text-[#003087] flex items-center gap-2"><PenLine className="h-5 w-5" /> Émargement</CardTitle>
        <CardDescription>Signez votre présence pour chaque demi-journée de formation (le jour même ou dans les 48 h).</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {items.map((d) => (
          <div key={d.dossier_id}>
            <p className="font-semibold text-sm text-[#003087] mb-2">{d.formation_code} — {d.formation_title} <span className="text-gray-500 font-normal">· session du {new Date(d.date_debut).toLocaleDateString("fr-FR")}{d.date_fin && d.date_fin !== d.date_debut ? ` au ${new Date(d.date_fin).toLocaleDateString("fr-FR")}` : ""}</span></p>
            <div className="space-y-1.5">
              {d.slots.map((s) => {
                const canSign = !s.signed && s.date <= today;
                const isOpen = signing === `${d.dossier_id}|${s.key}`;
                return (
                  <div key={s.key} className={`p-2.5 rounded-lg border ${s.signed ? "bg-green-50 border-green-200" : "bg-white border-gray-200"}`} data-testid={`slot-${d.dossier_id}-${s.key}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <p className="text-sm flex-1 capitalize">{frDate(s.date)} · <span className="font-medium">{s.demi === "matin" ? "Matin (9h00–12h30)" : "Après-midi (13h30–17h00)"}</span></p>
                      {s.signed ? (
                        <span className="text-xs text-green-700 font-semibold flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Signé le {new Date(s.signed_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</span>
                      ) : canSign ? (
                        <Button size="sm" className="bg-[#003087] hover:bg-[#002266] text-white" onClick={() => setSigning(`${d.dossier_id}|${s.key}`)} data-testid={`sign-btn-${d.dossier_id}-${s.key}`}>
                          <PenLine className="h-3.5 w-3.5 mr-1" /> Signer
                        </Button>
                      ) : (
                        <span className="text-xs text-gray-400">À venir</span>
                      )}
                    </div>
                    {isOpen && <SignaturePad onCancel={() => setSigning(null)} onConfirm={(sig) => sign(d.dossier_id, s.key, sig)} />}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
